import { richTextDocEquals, safeParseRichTextDoc } from '@lib/rich-text';
import Link from '@tiptap/extension-link';
import Placeholder from '@tiptap/extension-placeholder';
import TextAlign from '@tiptap/extension-text-align';
import Underline from '@tiptap/extension-underline';
import { EditorContent, useEditor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import { type FC, useEffect } from 'react';

import { characterLimit } from './character-limit';
import { listDepthLimit } from './list-depth-limit';
import { StyledBody, StyledContent, StyledCounter, StyledRoot } from './RichTextEditor.styled';
import { Toolbar } from './toolbar';
import type { RichTextEditorProps } from './types';

const ALLOWED_LINK_SCHEME = /^(https?|mailto):/i;
const HAS_SCHEME = /^[a-z][a-z0-9+.-]*:/i;

const DEFAULT_MIN_HEIGHT = 240;

const RichTextEditor: FC<RichTextEditorProps> = ({
  value = null,
  onChange,
  placeholder = '',
  disabled = false,
  minHeight = DEFAULT_MIN_HEIGHT,
  maxLength,
  ariaLabel,
  className,
}) => {
  const editor = useEditor({
    editable: !disabled,
    content: value ?? '',
    extensions: [
      StarterKit.configure({
        heading: { levels: [1, 2, 3] },
        blockquote: false,
        codeBlock: false,
        horizontalRule: false,
        link: false,
        underline: false,
      }),
      Underline,
      listDepthLimit,
      TextAlign.configure({ types: ['heading', 'paragraph'] }),
      Link.configure({
        openOnClick: false,
        autolink: true,
        linkOnPaste: false,
        isAllowedUri: (url, ctx) =>
          ctx.defaultValidate(url) &&
          ALLOWED_LINK_SCHEME.test(HAS_SCHEME.test(url) ? url : `${ctx.defaultProtocol}://${url}`),
      }),
      Placeholder.configure({ placeholder }),
      ...(maxLength === undefined ? [] : [characterLimit(maxLength)]),
    ],
    editorProps: {
      attributes: ariaLabel ? { 'aria-label': ariaLabel } : {},
    },
    onUpdate: ({ editor: current }) => {
      const parsed = safeParseRichTextDoc(current.getJSON());
      if (parsed.ok) onChange?.(parsed.doc);
    },
  });

  useEffect(() => {
    editor?.setEditable(!disabled);
  }, [editor, disabled]);

  useEffect(() => {
    if (!editor) return;
    const current = safeParseRichTextDoc(editor.getJSON());
    if (current.ok && richTextDocEquals(value, current.doc)) return;
    editor.commands.setContent(value ?? '', { emitUpdate: false });
  }, [editor, value]);

  if (!editor) return null;

  return (
    <StyledRoot className={className}>
      <StyledBody $disabled={disabled}>
        <StyledContent $minHeight={minHeight}>
          <EditorContent editor={editor} />
        </StyledContent>

        <Toolbar editor={editor} disabled={disabled} />

        {maxLength === undefined ? null : (
          <StyledCounter>
            {`${editor.state.doc.textContent.length.toLocaleString()} / ${maxLength.toLocaleString()} characters`}
          </StyledCounter>
        )}
      </StyledBody>
    </StyledRoot>
  );
};

RichTextEditor.displayName = 'RichTextEditor';

export default RichTextEditor;
